import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Calendar, CreditCard, User, AlertCircle, Printer, Eye, Trash2, CheckSquare, Square, Edit2, Landmark, FileSpreadsheet } from 'lucide-react';
import { exportToExcel } from '../../utils/excelExport';
import { Card, Button, Input, Select, Badge } from '../../components/ui/index.jsx';
import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const DepositReportList = () => {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const navigate = useNavigate();
    const [selectedReport, setSelectedReport] = useState(null);
    const [editReport, setEditReport] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('deposit-reports', 'create');
    const { success, error: toastError, warning } = useToast();
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [printDates, setPrintDates] = useState({
        from: new Date().toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0]
    });
    const [printLoading, setPrintLoading] = useState(false);

    const { data: reportsResponse, loading, refresh } = useGet(`/deposit-reports?page=${currentPage}&search=${searchTerm}`);
    const reports = reportsResponse?.data || [];
    const pagination = reportsResponse?.meta || null;
    const stats = reportsResponse?.stats || { total_amount: 0, pending_amount: 0, deposited_amount: 0 };

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا التقرير؟ سيتم فك ارتباط المدفوعات التابعة له لكن لن تحذف.')) {
            try {
                await api.delete(`/deposit-reports/${id}`);
                success('تم حذف التقرير بنجاح');
                refresh();
            } catch (err) {
                toastError('خطأ أثناء الحذف');
            }
        }
    };

    const handleViewDetails = async (report) => {
        try {
            const res = await api.get(`/deposit-reports/${report.id}`);
            setSelectedReport(res.data.data);
            setIsDetailsModalOpen(true);
        } catch (err) {
            toastError('خطأ أثناء جلب التفاصيل');
        }
    };

    const handleEdit = async (report) => {
        try {
            const res = await api.get(`/deposit-reports/${report.id}`);
            setEditReport(res.data.data);
            setIsFormModalOpen(true);
        } catch (err) {
            toastError('خطأ أثناء جلب بيانات التقرير');
        }
    };

    const handleExportExcel = async () => {
        try {
            const response = await api.get(`/deposit-reports?all=true&search=${searchTerm}`);
            const reportsToExport = response.data?.data || response.data || [];
            
            const data = reportsToExport.map(r => ({
                'رقم التقرير': r.report_number,
                'تاريخ التقرير': r.report_date,
                'عدد المدفوعات': r.payments_count,
                'المبلغ الإجمالي': r.total_amount,
                'وسيلة الدفع': r.payment_mode,
                'الحالة': r.bank_transaction_id ? 'تم الإيداع' : 'قيد الانتظار',
                'رقم العملية البنكية': r.bank_transaction_number || '--'
            }));
            
            exportToExcel(data, `تقارير_الإيداع_${new Date().toISOString().split('T')[0]}`);
        } catch (error) {
            console.error("Export failed:", error);
        }
    };

    const handlePrint = async (report) => {
        try {
            const res = await api.get(`/deposit-reports/${report.id}`);
            localStorage.setItem('print_report_details', JSON.stringify(res.data.data));
            window.open('/print/deposit-report', '_blank');
        } catch (err) {
            toastError('خطأ أثناء التحضير للطباعة');
        }
    };

    const handlePrintList = async () => {
        setPrintLoading(true);
        try {
            const queryParams = new URLSearchParams({
                all: 'true',
                date_from: printDates.from,
                date_to: printDates.to
            }).toString();
            
            const res = await api.get(`/deposit-reports?${queryParams}`);
            const data = res.data?.data || res.data || [];
            // Sort Oldest to Newest for print
            const sortedReports = [...data].sort((a, b) => new Date(a.report_date) - new Date(b.report_date));
            localStorage.setItem('print_deposit_reports_list', JSON.stringify({ 
                reports: sortedReports,
                filters: {
                    date_from: printDates.from,
                    date_to: printDates.to
                }
            }));
            window.open('/print/deposit-reports-list', '_blank');
            setIsPrintModalOpen(false);
        } catch (err) {
            toastError('خطأ أثناء جلب البيانات للطباعة');
        } finally {
            setPrintLoading(false);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-slate-800">تقارير الإيداع البنكي</h2>
                    <p className="text-slate-400 text-[11px] leading-none">إدارة ومجوعة المدفوعات المودعة في البنك</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button 
                        variant="outline" 
                        onClick={handleExportExcel}
                        className="border-slate-200 text-slate-600"
                    >
                        <FileSpreadsheet className="w-4 h-4 ml-2 text-green-600" />
                        تحميل Excel
                    </Button>
                    <Button 
                        variant="secondary" 
                        onClick={() => setIsPrintModalOpen(true)} 
                        isLoading={printLoading}
                        className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200"
                    >
                        <Printer className="w-4 h-4 ml-2" />
                        طباعة التقارير
                    </Button>
                    {canCreate && (
                        <Button onClick={() => { setEditReport(null); setIsFormModalOpen(true); }} className="shadow-lg shadow-primary/20">
                            <Plus className="w-4 h-4 ml-2" />
                            تقرير إيداع جديد
                        </Button>
                    )}
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 bg-white border-slate-100 shadow-sm relative overflow-hidden group">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                            <FileText size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">قيمة المجموع</p>
                            <h3 className="text-lg font-bold text-slate-800">{(stats.total_amount || 0).toLocaleString()} د.م</h3>
                        </div>
                    </div>
                </Card>

                <Card className="p-4 bg-white border-slate-100 shadow-sm relative overflow-hidden group">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                            <AlertCircle size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">قيد الانتظار</p>
                            <h3 className="text-lg font-bold text-slate-800">{(stats.pending_amount || 0).toLocaleString()} د.م</h3>
                        </div>
                    </div>
                </Card>

                <Card className="p-4 bg-white border-slate-100 shadow-sm relative overflow-hidden group">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                            <Landmark size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">تم الايداع</p>
                            <h3 className="text-lg font-bold text-slate-800">{(stats.deposited_amount || 0).toLocaleString()} د.م</h3>
                        </div>
                    </div>
                </Card>
            </div>

            <Card className="p-4 md:p-6 bg-white/50 backdrop-blur-sm border-slate-100">
                <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="flex-1 w-full">
                        <Input 
                            placeholder="بحث برقم التقرير (مثال: 2026/00001)..." 
                            icon={Search} 
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>
            </Card>

            <Card className="overflow-hidden border-slate-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-right">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">رقم التقرير</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">تاريخ التقرير</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">المبلغ الإجمالي</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">وسيلة الدفع</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">صاحب العملية</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">الحالة</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-left w-32">الإجراءات</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="7" className="py-12 text-center animate-pulse">جاري التحميل...</td></tr>
                        ) : reports.length === 0 ? (
                            <tr><td colSpan="7" className="py-12 text-center text-slate-400 italic text-sm">لا توجد تقارير مسجلة حالياً</td></tr>
                        ) : (
                            reports.map(report => (
                                <tr key={report.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4 text-sm font-bold text-slate-700">{report.report_number}</td>
                                    <td className="px-6 py-4 text-sm text-slate-600">{new Date(report.report_date).toLocaleDateString('ar-MA')}</td>
                                    <td className="px-6 py-4 text-sm font-bold text-primary">{report.total_amount} د.م</td>
                                    <td className="px-6 py-4">
                                        <Badge variant={report.payment_mode === 'CHEQUE' ? 'warning' : 'success'}>
                                            {report.payment_mode}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><User size={10} /></div>
                                            <span className="text-[11px] text-slate-600 font-medium">{report.user_name || '--'}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {report.bank_transaction_id ? (
                                            <Badge variant="success">تم الإيداع</Badge>
                                        ) : (
                                            <Badge variant="warning">قيد الانتظار</Badge>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-2">
                                            <button 
                                                onClick={() => handleViewDetails(report)}
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/5 transition-all"
                                                title="عرض التفاصيل"
                                            >
                                                <Eye size={16} />
                                            </button>
                                            {report.bank_transaction_id && (
                                                <button 
                                                    onClick={() => navigate(`/dashboard/bank?search=${report.bank_transaction_number || `#TX-${report.bank_transaction_id}`}`)}
                                                    className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/5 transition-all"
                                                    title="الانتقال إلى العملية البنكية"
                                                >
                                                    <Landmark size={16} />
                                                </button>
                                            )}
                                            <button 
                                                onClick={() => handlePrint(report)}
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 transition-all"
                                                title="طباعة"
                                            >
                                                <Printer size={16} />
                                            </button>
                                            {!report.bank_transaction_id && (
                                                <>
                                                    <button 
                                                        onClick={() => handleEdit(report)}
                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-all"
                                                        title="تعديل"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDelete(report.id)}
                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                                                        title="حذف"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>

                {pagination && pagination.last_page > 1 && (
                    <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-end gap-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >السابق</Button>
                        <span className="text-xs text-slate-500">صفحة {currentPage} من {pagination.last_page}</span>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                            disabled={currentPage === pagination.last_page}
                        >التالي</Button>
                    </div>
                )}
            </Card>

            <Modal 
                isOpen={isFormModalOpen} 
                onClose={() => setIsFormModalOpen(false)} 
                title={editReport ? `تعديل تقرير الإيداع: ${editReport.report_number}` : "إنشاء تقرير إيداع جديد"}
                size="5xl"
            >
                <ReportForm 
                    report={editReport} 
                    onSuccess={() => { setIsFormModalOpen(false); refresh(); }} 
                />
            </Modal>

            <Modal 
                isOpen={isDetailsModalOpen} 
                onClose={() => setIsDetailsModalOpen(false)} 
                title={`تفاصيل تقرير الإيداع: ${selectedReport?.report_number}`}
                size="4xl"
            >
                {selectedReport && <ReportDetails report={selectedReport} />}
            </Modal>

            {/* Modal for printing with date filter */}
            <Modal
                isOpen={isPrintModalOpen}
                onClose={() => setIsPrintModalOpen(false)}
                title="طباعة تقارير الإيداع"
                size="md"
            >
                <div className="space-y-6 text-right" dir="rtl">
                    <p className="text-sm text-slate-500">اختر الفترة الزمنية للتقارير التي تود طباعتها:</p>
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label="من تاريخ" 
                            type="date" 
                            value={printDates.from}
                            onChange={e => setPrintDates({ ...printDates, from: e.target.value })}
                            icon={Calendar}
                        />
                        <Input 
                            label="إلى تاريخ" 
                            type="date" 
                            value={printDates.to}
                            onChange={e => setPrintDates({ ...printDates, to: e.target.value })}
                            icon={Calendar}
                        />
                    </div>
                    <div className="flex justify-end pt-4 border-t border-slate-100">
                        <Button 
                            onClick={handlePrintList} 
                            isLoading={printLoading}
                            className="px-8"
                        >
                            <Printer className="w-4 h-4 ml-2" />
                            تأكيد الطباعة
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const ReportForm = ({ report, onSuccess }) => {
    const [allPayments, setAllPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [paymentSearch, setPaymentSearch] = useState('');
    const [paymentModeFilter, setPaymentModeFilter] = useState('');
    const { success, error: toastError, warning } = useToast();
    const { selectedYear } = useAuth();

    // Filter payments based on search and mode
    const filteredPayments = React.useMemo(() => {
        let result = allPayments;

        if (!paymentModeFilter) return []; // Hide payments until mode is selected

        if (paymentModeFilter) {
            result = result.filter(p => {
                const modeName = p.payment_mode_details?.name || p.payment_mode || '';
                return modeName === paymentModeFilter;
            });
        }

        if (paymentSearch.trim()) {
            const search = paymentSearch.toLowerCase();
            result = result.filter(p => 
                p.student?.full_name?.toLowerCase().includes(search) ||
                p.amount?.toString().includes(search) ||
                new Date(p.payment_date).toLocaleDateString('ar-MA').includes(search)
            );
        }

        return result;
    }, [allPayments, paymentSearch, paymentModeFilter]);

    useEffect(() => {
        if (report) {
            setReportDate(report.report_date);
            setNotes(report.notes || '');
            setSelectedIds(report.payments?.map(p => p.id) || []);
            if (report.payments && report.payments.length > 0) {
                const modeName = report.payments[0].payment_mode_details?.name || report.payments[0].payment_mode;
                setPaymentModeFilter(modeName || '');
            }
        }
        fetchPayments();
    }, [report]);

    const fetchPayments = async () => {
        try {
            // Fetch pending payments
            const res = await api.get('/deposit-reports/pending');
            let _val = res.data?.data || res.data || [];
            let fetchedPending = Array.isArray(_val) ? _val : [];
            
            let finalPayments = [...fetchedPending];
            
            // If editing, merge payments currently in the report
            if (report && Array.isArray(report.payments)) {
                // Ensure we don't have duplicates
                const reportPaymentIds = report.payments.map(p => p.id);
                const filteredPending = fetchedPending.filter(p => !reportPaymentIds.includes(p.id));
                
                finalPayments = [...report.payments, ...filteredPending];
            }
            
            setAllPayments(finalPayments);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelection = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const toggleAll = () => {
        const filteredIds = filteredPayments.map(p => p.id);
        const allFilteredSelected = filteredIds.every(id => selectedIds.includes(id));

        if (allFilteredSelected && filteredIds.length > 0) {
            // Unselect only those in the current filtered list
            setSelectedIds(prev => prev.filter(id => !filteredIds.includes(id)));
        } else {
            // Select those in the current filtered list (avoid duplicates)
            setSelectedIds(prev => [...new Set([...prev, ...filteredIds])]);
        }
    };

    const totalSelected = allPayments
        .filter(p => selectedIds.includes(p.id))
        .reduce((sum, p) => sum + parseFloat(p.amount), 0);

    const handleSubmit = async () => {
        if (selectedIds.length === 0) return warning('يرجى اختيار المدفوعات أولاً');

        // Validate date against selected year
        if (selectedYear && reportDate) {
            if (reportDate < selectedYear.start_date || reportDate > selectedYear.end_date) {
                toastError(`تاريخ التقرير يجب أن يكون بين ${selectedYear.start_date} و ${selectedYear.end_date}`);
                return;
            }
        }

        setSubmitting(true);
        try {
            if (report) {
                await api.put(`/deposit-reports/${report.id}`, {
                    report_date: reportDate,
                    notes,
                    payment_ids: selectedIds
                });
            } else {
                await api.post('/deposit-reports', {
                    report_date: reportDate,
                    notes,
                    payment_ids: selectedIds
                });
                success('تم إنشاء التقرير بنجاح');
            }
            onSuccess();
        } catch (err) {
            toastError('خطأ أثناء حفظ التقرير');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="md:col-span-1">
                    <Input 
                        label="تاريخ الإيداع" 
                        type="date" 
                        value={reportDate} 
                        onChange={e => setReportDate(e.target.value)} 
                        icon={Calendar}
                    />
                </div>
                <div className="md:col-span-1">
                    <Select 
                        label={<span>وسيلة الدفع <span className="text-red-500">*</span></span>}
                        value={paymentModeFilter}
                        onChange={(e) => {
                            setPaymentModeFilter(e.target.value);
                            setSelectedIds([]); 
                        }}
                        options={[
                            {label: '-- اختر وسيلة --', value: ''},
                            ...[...new Set(allPayments.map(p => p.payment_mode_details?.name || p.payment_mode).filter(Boolean))].map(mode => ({ label: mode, value: mode }))
                        ]}
                        className={!paymentModeFilter ? 'border-amber-300 bg-amber-50/30' : ''}
                    />
                </div>
                <div className="md:col-span-2">
                    <Input 
                        label="ملاحظات (اختياري)" 
                        value={notes} 
                        onChange={e => setNotes(e.target.value)} 
                        placeholder="أدخل أي ملاحظات إضافية..."
                    />
                </div>
                <div className="md:col-span-1 flex flex-col justify-end">
                    <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 text-center">
                        <span className="text-[10px] text-primary uppercase block">الإجمالي المحدد</span>
                        <span className="text-xl font-bold text-primary">{totalSelected.toLocaleString()} د.م</span>
                    </div>
                </div>
            </div>

            <div className="space-y-3">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-2">
                    <h3 className="text-xs font-bold text-slate-500 uppercase">قائمة المدفوعات المتاحة</h3>
                    <div className="flex items-center gap-3">

                        <div className="w-64">
                            <Input 
                                placeholder="بحث في المدفوعات..." 
                                icon={Search} 
                                value={paymentSearch} 
                                onChange={e => setPaymentSearch(e.target.value)} 
                            />
                        </div>
                    </div>
                </div>
                
                <div className="border border-slate-100 rounded-xl overflow-hidden max-h-[500px] shadow-inner bg-slate-50/20 flex flex-col">
                    <div className="overflow-y-auto overflow-x-auto h-full text-right w-full block">
                        <table className="w-full text-right">
                        <thead className="sticky top-0 bg-white z-10 shadow-sm">
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="px-6 py-3 w-10">
                                    <button type="button" onClick={toggleAll} className="text-primary hover:bg-primary/5 p-1 rounded transition-colors">
                                        {filteredPayments.length > 0 && filteredPayments.every(p => selectedIds.includes(p.id)) ? <CheckSquare size={20} /> : <Square size={20} />}
                                    </button>
                                </th>
                                <th className="px-6 py-3 text-[11px] font-bold text-slate-400">تاريخ الدفع</th>
                                <th className="px-6 py-3 text-[11px] font-bold text-slate-400">الطالب</th>
                                <th className="px-6 py-3 text-[11px] font-bold text-slate-400">الوسيلة</th>
                                <th className="px-6 py-3 text-[11px] font-bold text-slate-400">المبلغ</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-arabic">
                            {loading ? (
                                <tr><td colSpan="5" className="py-8 text-center animate-pulse text-sm">جاري جلب المدفوعات...</td></tr>
                            ) : !paymentModeFilter ? (
                                <tr><td colSpan="5" className="py-12 text-center text-amber-500 font-medium">يرجى اختيار وسيلة الدفع أولاً لتحديد المدفوعات المراد إيداعها</td></tr>
                            ) : filteredPayments.length === 0 ? (
                                <tr><td colSpan="5" className="py-8 text-center text-slate-400 text-sm">لا توجد نتائج تطابق بحثك</td></tr>
                            ) : (
                                (filteredPayments.map(payment => {
                                    const modeName = payment.payment_mode_details?.name;
                                    const isCheque = modeName === 'CHEQUE';
                                    const isEspece = modeName === 'ESPECE';
                                    const isSelected = selectedIds.includes(payment.id);

                                    return (
                                        <tr 
                                            key={payment.id} 
                                            className={`cursor-pointer transition-all border-b border-slate-100/50 relative ${
                                                isSelected 
                                                    ? 'bg-primary/5' 
                                                    : 'hover:bg-slate-50/80 bg-white'
                                            } ${
                                                isCheque 
                                                    ? 'border-r-4 border-r-blue-500' 
                                                    : isEspece 
                                                        ? 'border-r-4 border-r-emerald-500'
                                                        : 'border-r-4 border-r-slate-200'
                                            }`} 
                                            onClick={() => toggleSelection(payment.id)}
                                        >
                                            <td className="px-6 py-2">
                                                <div className={isSelected ? 'text-primary' : 'text-slate-300'}>
                                                    {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-xs">{new Date(payment.payment_date).toLocaleDateString('ar-MA')}</td>
                                            <td className="px-6 py-3">
                                                <span className="text-[13px] font-medium text-slate-700">{payment.student?.full_name}</span>
                                            </td>
                                            <td className="px-6 py-3 text-xs text-slate-500">
                                                <Badge variant={isCheque ? 'warning' : isEspece ? 'success' : 'outline'}>
                                                    {modeName || payment.payment_mode}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-2 text-xs font-bold text-slate-700">{payment.amount} د.م</td>
                                        </tr>
                                    );
                                }))
                            )}
                        </tbody>
                    </table>
                    </div>
                </div>
            </div>

            {!paymentModeFilter && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <AlertCircle size={14} className="text-amber-500 shrink-0" />
                    <span className="text-xs text-amber-700">يرجى اختيار وسيلة الدفع (إلزامي) قبل إنشاء تقرير الإيداع ليتم تحديد العمليات المتعلقة بها الممكن إضافتها للتقرير.</span>
                </div>
            )}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <span className="text-xs text-slate-400">تم تحديد <span className="text-primary font-bold">{selectedIds.length}</span> من أصل <span className="font-bold">{filteredPayments.length}</span> مدفوعات معروضة</span>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-blue-100/50 border border-blue-200"></div>
                        <span className="text-[10px] text-slate-500">شيك</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-emerald-100/50 border border-emerald-200"></div>
                        <span className="text-[10px] text-slate-500">نقداً</span>
                    </div>
                </div>
                <Button 
                    onClick={handleSubmit} 
                    isLoading={submitting} 
                    disabled={selectedIds.length === 0 || !paymentModeFilter}
                    className="px-8 shadow-lg shadow-primary/20"
                >
                    {report ? "تحديث التقرير" : "تأكيد وإنشاء التقرير"}
                </Button>
            </div>
        </div>
    );
};

const ReportDetails = ({ report }) => {
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div>
                    <span className="text-[10px] text-slate-400 uppercase block mb-1">تاريخ الإيداع</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(report.report_date).toLocaleDateString('ar-MA')}</span>
                </div>
                <div>
                    <span className="text-[10px] text-slate-400 uppercase block mb-1">المبلغ الإجمالي</span>
                    <span className="text-sm font-bold text-primary">{report.total_amount} د.م</span>
                </div>
                <div>
                    <span className="text-[10px] text-slate-400 uppercase block mb-1">عدد العمليات</span>
                    <span className="text-sm font-bold text-slate-700">{report.payments_count}</span>
                </div>
                <div>
                    <span className="text-[10px] text-slate-400 uppercase block mb-1">تاريخ الإنشاء</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(report.created_at).toLocaleDateString('ar-MA')}</span>
                </div>
            </div>

            {report.notes && (
                <div className="p-3 bg-white border border-slate-100 rounded-xl">
                    <span className="text-[10px] text-slate-400 uppercase block mb-1">ملاحظات</span>
                    <p className="text-sm text-slate-600 italic">{report.notes}</p>
                </div>
            )}

            <div className="border border-slate-100 rounded-xl overflow-hidden max-h-[300px] overflow-y-auto font-arabic">
                <table className="w-full text-right">
                    <thead className="sticky top-0 bg-white shadow-sm z-10">
                        <tr className="bg-slate-50/50">
                            <th className="px-6 py-3 text-[11px] font-bold text-slate-400">تاريخ الدفع</th>
                            <th className="px-6 py-3 text-[11px] font-bold text-slate-400">الطالب</th>
                            <th className="px-6 py-3 text-[11px] font-bold text-slate-400">الوسيلة</th>
                            <th className="px-6 py-3 text-[11px] font-bold text-slate-400 text-left">المبلغ</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {report.payments?.map(p => {
                            const modeName = p.payment_mode_details?.name;
                            const isCheque = modeName === 'CHEQUE';
                            const isEspece = modeName === 'ESPECE';
                            
                            return (
                                <tr key={p.id} className={`${isCheque ? 'bg-blue-50/30' : isEspece ? 'bg-emerald-50/30' : 'hover:bg-slate-50/30'} transition-colors`}>
                                    <td className="px-6 py-3 text-xs">{new Date(p.payment_date).toLocaleDateString('ar-MA')}</td>
                                    <td className="px-6 py-3 text-[13px] font-medium text-slate-700">{p.student?.full_name}</td>
                                    <td className="px-6 py-3 text-xs">
                                        <Badge variant={isCheque ? 'warning' : isEspece ? 'success' : 'outline'}>
                                            {modeName || p.payment_mode}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-3 text-xs font-bold text-slate-700 text-left">{p.amount} د.م</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default DepositReportList;
